/**
 * Data Timeline View Component
 *
 * Displays session-based data fetch timeline with session selector,
 * account cards, and chronological events using Equal DS components.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Timeline } from 'equal-ds-ui';
import { Clock, Landmark, Copy } from 'lucide-react';
import { SessionSelector } from './SessionSelector';
import { WebhookDetailModal } from './WebhookDetailModal';
import type { SessionTimeline, TimelineEvent } from '../../../types/timeline';
import type { TransformedDataSession } from '../../../types/consent';

interface DataTimelineViewProps {
  sessions: TransformedDataSession[];
  sessionTimeline: SessionTimeline | null;
  selectedSessionId: string;
  onSessionSelect: (sessionId: string) => void;
}

// AccountCard component for account selection
interface AccountCardProps {
  account: TransformedDataSession['accounts'][0];
  isSelected: boolean;
  onClick: () => void;
}

export const DataTimelineView: React.FC<DataTimelineViewProps> = ({
  sessions,
  sessionTimeline,
  selectedSessionId,
  onSessionSelect,
}) => {
  const [selectedAccountIndex, setSelectedAccountIndex] = useState<number>(0);
  const [selectedWebhook, setSelectedWebhook] = useState<TimelineEvent | null>(null);

  // Reset selected account when session changes
  useEffect(() => {
    setSelectedAccountIndex(0);
  }, [selectedSessionId]);

  // Inject "View Details" action into webhook events so button appears inline in timeline row
  const eventsWithActions = useMemo(() => {
    if (!sessionTimeline) return [];
    return sessionTimeline.events.map(event => {
      const isWebhook = (event.rawData?.event_type as string)?.startsWith('WEBHOOK_');
      if (!isWebhook) return event;
      return {
        ...event,
        action: {
          type: 'button' as const,
          label: 'View Details',
          onClick: () => setSelectedWebhook(event),
        },
      };
    });
  }, [sessionTimeline]);

  // Handle empty sessions
  if (sessions.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-text-muted">
        <p>No data sessions available for this consent.</p>
      </div>
    );
  }

  // Get currently selected session
  const currentSession = sessions.find((s) => s.sessionId === selectedSessionId);

  // Get accounts from current session
  const accounts = currentSession?.accounts || [];

  // Handle no timeline data
  if (!sessionTimeline || sessionTimeline.events.length === 0) {
    return (
      <div className="flex flex-col h-full space-y-3">
        {/* Instruction Text */}
        <p className="text-sm text-text-muted">
          Select a session and then a related account to view its data timeline:
        </p>

        {/* Session Selection */}
        {sessions.length > 1 && (
          <SessionSelector
            sessions={sessions}
            selectedSessionId={selectedSessionId}
            onSessionSelect={onSessionSelect}
          />
        )}

        {/* Empty State */}
        <div className="flex-1 flex items-center justify-center text-text-tertiary">
          <p>No timeline data available for this session.</p>
        </div>
      </div>
    );
  }

  // Copy session ID to clipboard
  const copySessionId = () => {
    navigator.clipboard.writeText(selectedSessionId);
  };


  // AccountCard component for account selection
  const AccountCard: React.FC<AccountCardProps> = ({ account, isSelected, onClick }) => (
    <div
      className={`flex items-center gap-2 px-4 py-3 rounded-lg cursor-pointer
        min-w-[200px] border-2 transition-all flex-shrink-0
        ${
          isSelected
            ? 'bg-primary-500 text-white border-primary-600'
            : 'bg-background-secondary border-border-primary hover:border-primary-300'
        }`}
      onClick={onClick}
    >
      <Landmark className="w-5 h-5 flex-shrink-0" />
      <div className="flex flex-col min-w-0">
        <span className="font-medium text-sm truncate">{account.accountType}</span>
        <span className={`text-xs ${isSelected ? 'opacity-90' : 'opacity-70'}`}>
          {account.accountNumber}
        </span>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full space-y-3">
      {/* Instruction Text */}
      <p className="text-sm text-text-muted">
        Select a session and then a related account to view its data timeline:
      </p>

      {/* Session Selection - Only show if multiple sessions */}
      {sessions.length > 1 && (
        <SessionSelector
          sessions={sessions}
          selectedSessionId={selectedSessionId}
          onSessionSelect={onSessionSelect}
        />
      )}

      {/* Selected Session ID with Copy */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-text-muted">Session ID:</span>
        <span className="text-text-primary font-mono">{selectedSessionId}</span>
        <button
          onClick={copySessionId}
          className="p-1 hover:bg-background-secondary rounded transition-colors"
          title="Copy Session ID"
        >
          <Copy className="w-4 h-4 text-text-muted" />
        </button>
      </div>

      {/* Account Selection - Horizontal Scrollable Cards */}
      {accounts.length > 0 && (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {accounts.map((account, index) => (
            <AccountCard
              key={`${account.fipId}-${account.linkRefNumber}-${index}`}
              account={account}
              isSelected={index === selectedAccountIndex}
              onClick={() => setSelectedAccountIndex(index)}
            />
          ))}
        </div>
      )}

      {/* Data Timeline Section */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Timeline Header with TAT */}
        <div className="flex items-center justify-between mb-1.5 flex-shrink-0">
          <h3 className="text-sm font-medium text-text-primary tracking-wider">DATA TIMELINE</h3>
          {sessionTimeline.overallTAT?.duration && (
            <div className="flex items-center gap-1">
              <span className="text-sm font-medium text-text-muted tracking-wider">
                OVERALL TAT:
              </span>
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3 text-text-primary" />
                <span className="text-sm font-medium text-text-primary tracking-wide">
                  {sessionTimeline.overallTAT.duration}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Timeline — webhook events get inline "View Details" button */}
        <div className="flex-1 min-h-0 bg-background-secondary border border-border-light rounded-xl p-4 overflow-y-auto">
          <Timeline
            items={eventsWithActions as any}
            variant="default"
            showConnectors={true}
            connectorGap={2}
            className="!justify-start gap-6"
          />
        </div>
      </div>

      {/* Modal */}
      {selectedWebhook && (
        <WebhookDetailModal event={selectedWebhook} onClose={() => setSelectedWebhook(null)} />
      )}
    </div>
  );
};
